import * as React from "react";
import { invariant } from "@plans/appkit";
import type { DialogManager } from "@plans/appkit";

export function useDialog() {
	const dialogManager = React.useContext(DialogContext);
	invariant(dialogManager, "Use within a DialogProvider");

	const currentDialog = React.useSyncExternalStore(
		(onStoreChange) => dialogManager.subscribe("change", onStoreChange),
		() => dialogManager.currentDialog,
		() => dialogManager.currentDialog,
	);

	const alert = React.useCallback(
		(...args: Parameters<typeof dialogManager.alert>) =>
			dialogManager.alert(...args),
		[dialogManager],
	);

	const confirm = React.useCallback(
		(...args: Parameters<typeof dialogManager.confirm>) =>
			dialogManager.confirm(...args),
		[dialogManager],
	);

	const prompt = React.useCallback(
		(...args: Parameters<typeof dialogManager.prompt>) =>
			dialogManager.prompt(...args),
		[dialogManager],
	);

	const ok = React.useCallback(
		(...args: Parameters<typeof dialogManager.ok>) => dialogManager.ok(...args),
		[dialogManager],
	);

	const cancel = React.useCallback(
		(...args: Parameters<typeof dialogManager.cancel>) =>
			dialogManager.cancel(...args),
		[dialogManager],
	);

	return {
		alert,
		confirm,
		prompt,
		ok,
		cancel,
		currentDialog,
	};
}

export const DialogContext = React.createContext<
	DialogManager<React.ReactNode | string> | undefined
>(undefined);
